import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  BOT_OUTCOME_LABEL, formatIst, formatIstDate, inboxApi,
  type BotReportDay, type BotReportStep,
} from '@wa/shared';

/**
 * The Bots tab of Reports.
 *
 * The Bots screen's panel answers "how is it going"; this answers what follows:
 * is it better than the period before, what do customers come for, what did they
 * get, when do they come, and which conversations should somebody open. Every
 * number is one row per finished conversation, so the whole report is one read.
 *
 * Steps are named with the builder's own labels — "Which product is this for?" and
 * not fetch_products — because a report that needs a developer to read it is a
 * report nobody reads.
 */
export function BotReport({ from, to }: { from: string; to: string }) {
  const [which, setWhich] = useState<string | undefined>(undefined);

  const report = useQuery({
    queryKey: ['report-bots', from, to, which ?? ''],
    queryFn: () => inboxApi.reportBots(from, to, which),
  });

  const data = report.data;

  if (report.isPending) {
    return <p className="text-body text-(--color-muted)">Counting…</p>;
  }

  if (!data || !data.name) {
    return (
      <section className="rounded-lg border border-(--color-line) bg-(--color-surface) px-4 py-8 text-center">
        <p className="text-body text-(--color-muted)">No bot has run in this range.</p>
        <p className="mt-1 text-small text-(--color-muted)">
          Switch one on under <Link to="/bots" className="font-medium text-(--color-brand) hover:underline">Bots</Link> and this fills in as customers talk to it.
        </p>
      </section>
    );
  }

  const name = data.name;
  const previous = data.previous;

  return (
    <div className="flex flex-col gap-5">
      {data.names.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-small text-(--color-muted)">Bot</span>
          <div role="tablist" aria-label="Bot" className="chip-scroll flex items-center gap-1">
            {data.names.map((n) => (
              <button
                key={n}
                type="button"
                role="tab"
                aria-selected={n === name}
                onClick={() => setWhich(n)}
                className={[
                  'tap shrink-0 rounded-(--radius-chip) px-2.5 py-1 text-micro font-medium transition',
                  n === name
                    ? 'bg-(--color-sunken) text-(--color-ink)'
                    : 'text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)',
                ].join(' ')}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      )}

      {data.total === 0 ? (
        <section className="rounded-lg border border-(--color-line) bg-(--color-surface) px-4 py-8 text-center">
          <p className="text-body text-(--color-muted)">
            {name} had no finished conversations between {formatIstDate(data.from)} and {formatIstDate(data.to)}.
          </p>
        </section>
      ) : (
        <>
          <section className="grid gap-3 @min-[30rem]:grid-cols-2 @min-[48rem]:grid-cols-4">
            <Figure label="Conversations" value={data.total} previous={previous?.total} />
            <Figure label="Finished the flow" value={data.completed} previous={previous?.completed} share={data.total} tone="good" />
            <Figure label="Passed to a person" value={data.handedOff} previous={previous?.handedOff} share={data.total} neutral />
            <Figure label="Stopped part-way" value={data.leftEarly} previous={previous?.leftEarly} share={data.total} tone="bad" lowerIsBetter />
          </section>

          <section className="rounded-lg border border-(--color-line) bg-(--color-surface) py-3">
            <div className="flex flex-wrap items-baseline gap-x-3 px-3 sm:px-4">
              <h2 className="text-body font-semibold">Conversations a day</h2>
              <p className="text-micro text-(--color-muted)">
                Typically {data.medianTurns ?? 0} {data.medianTurns === 1 ? 'reply' : 'replies'} from the customer
                {data.medianMinutes != null && <> and about {describeMinutes(data.medianMinutes)} to finish</>}.
              </p>
            </div>
            <DayChart days={data.days ?? []} />
          </section>

          <section className="grid gap-3 @min-[40rem]:grid-cols-2">
            <StepList
              title="What customers got"
              hint="The step a finished conversation ended on — a ticket raised, a video sent, a registration link."
              steps={data.endings ?? []}
              of={data.completed}
              colour="var(--chart-1)"
              empty="No conversation reached an end step in this range."
            />
            <StepList
              title="Where people stop"
              hint="The step they were on when they went quiet, or when the bot gave up on their answer. A step near the top is usually a question worth rewording."
              steps={data.dropOff ?? []}
              of={data.leftEarly}
              colour="var(--color-bad)"
              empty="Nobody stopped part-way in this range."
            />
          </section>

          <section className="grid gap-3 @min-[40rem]:grid-cols-2">
            <StepList
              title="How far people get"
              hint="How many conversations reached each step, counted once each — the shape of the funnel."
              steps={(data.reached ?? []).slice(0, 12)}
              of={data.total}
              colour="var(--chart-2)"
              empty="No steps recorded."
            />
            <HoursChart hours={data.hours ?? []} total={data.total} />
          </section>

          {data.byOutcome && Object.keys(data.byOutcome).length > 0 && (
            <section className="rounded-lg border border-(--color-line) bg-(--color-surface) px-3 py-3 sm:px-4">
              <h2 className="text-body font-semibold">How conversations ended</h2>
              <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
                {Object.entries(data.byOutcome)
                  .sort((a, b) => b[1] - a[1])
                  .map(([code, count]) => (
                    <li key={code} className="text-small">
                      <span className="text-(--color-muted)">{BOT_OUTCOME_LABEL[code] ?? code}</span>{' '}
                      <span className="font-medium tabular-nums">{count.toLocaleString('en-IN')}</span>
                    </li>
                  ))}
              </ul>
              {data.problems > 0 && (
                <p className="mt-2 text-small text-(--color-warn)">
                  {data.problems.toLocaleString('en-IN')} ended because of an error, a delivery refusal or the message cap —
                  the platform's side, not the customer's. Those are worth opening below.
                </p>
              )}
            </section>
          )}

          {data.recent && data.recent.length > 0 && (
            <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
              <div className="px-3 pt-3 sm:px-4">
                <h2 className="text-body font-semibold">Latest conversations</h2>
                <p className="mt-0.5 text-micro text-(--color-muted)">The newest {data.recent.length}. Open one to read it.</p>
              </div>
              <ul className="mt-2 divide-y divide-(--color-line)">
                {data.recent.map((r) => (
                  <li key={`${r.conversationId}-${r.endedAt}`} className="flex items-center gap-3 px-3 py-2 sm:px-4">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-body">
                        {r.displayName || `+${r.waId}`}
                        {r.displayName && <span className="ml-1.5 text-micro text-(--color-faint)">+{r.waId}</span>}
                      </p>
                      <p className="truncate text-micro text-(--color-muted)">
                        <Outcome code={r.outcome} />
                        {r.lastLabel && <> · {r.lastLabel}</>}
                        {' '}· {r.turns} {r.turns === 1 ? 'reply' : 'replies'}
                      </p>
                    </div>
                    <span className="shrink-0 text-micro tabular-nums text-(--color-faint)">{formatIst(r.endedAt)}</span>
                    <Link
                      to={`/inbox?open=${r.conversationId}`}
                      className="tap shrink-0 rounded-(--radius-chip) border border-(--color-line) px-2 py-0.5 text-micro font-medium text-(--color-brand) hover:bg-(--color-sunken)"
                    >
                      Open
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Outcome({ code }: { code: string }) {
  const tone = code === 'completed' ? 'text-(--color-good)'
    : code === 'abandoned' || code === 'gave_up' ? 'text-(--color-bad)'
    : code === 'failed' || code === 'send_cap' || code === 'undeliverable' ? 'text-(--color-warn)'
    : 'text-(--color-muted)';
  return <span className={`font-medium ${tone}`}>{BOT_OUTCOME_LABEL[code] ?? code}</span>;
}

/**
 * A headline figure with its share of conversations and the arrow against the
 * period before. The arrow is coloured by whether the move is good, which for
 * "stopped part-way" means down.
 */
function Figure({ label, value, previous, share, tone, lowerIsBetter, neutral }: {
  label: string; value: number; previous?: number; share?: number;
  tone?: 'good' | 'bad'; lowerIsBetter?: boolean;
  /** A move in either direction is just a fact — passing more chats to people is not good or bad by itself. */
  neutral?: boolean;
}) {
  const pct = share && share > 0 ? Math.round((value / share) * 100) : null;
  const change = previous === undefined || previous === 0 ? null : Math.round(((value - previous) / previous) * 100);
  const improved = change === null || neutral ? null : lowerIsBetter ? change < 0 : change > 0;

  return (
    <div className="rounded-lg border border-(--color-line) bg-(--color-surface) px-3 py-2.5">
      <p className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">{label}</p>
      <p className={`mt-0.5 text-title font-semibold tabular-nums ${tone === 'good' && value > 0 ? 'text-(--color-good)' : tone === 'bad' && value > 0 ? 'text-(--color-bad)' : ''}`}>
        {value.toLocaleString('en-IN')}
        {pct !== null && <span className="ml-1.5 text-small font-normal text-(--color-muted)">{pct}%</span>}
      </p>
      <p className="mt-0.5 text-micro leading-snug text-(--color-muted)">
        {change === null
          ? previous === 0 ? 'None in the period before.' : 'No earlier period to compare.'
          : (
            <span className={improved === null || change === 0 ? '' : improved ? 'text-(--color-good)' : 'text-(--color-bad)'}>
              {change > 0 ? '↑' : change < 0 ? '↓' : '→'} {Math.abs(change)}% on the period before ({previous!.toLocaleString('en-IN')})
            </span>
          )}
      </p>
    </div>
  );
}

const DAY_SERIES = [
  { key: 'completed' as const, label: 'Finished', colour: 'var(--chart-1)' },
  { key: 'handedOff' as const, label: 'Passed to a person', colour: 'var(--chart-2)' },
  { key: 'leftEarly' as const, label: 'Stopped', colour: 'var(--color-bad)' },
  { key: 'problems' as const, label: 'Error', colour: 'var(--color-warn)' },
];

/**
 * Conversations a day, stacked by how they ended. Bars rather than lines because
 * a day is a count of whole conversations, and a stack because the question is
 * "of Tuesday's forty, how many finished" — one axis, the four outcomes always in
 * the same order and the same colour, a legend always present.
 */
function DayChart({ days }: { days: BotReportDay[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const titleId = useId();

  if (days.length === 0) return null;

  const W = 720;
  const H = 200;
  const PAD = { top: 12, right: 12, bottom: 26, left: 36 };
  const peak = Math.max(1, ...days.map((d) => d.total));
  const ceiling = niceCeiling(peak);
  const slot = (W - PAD.left - PAD.right) / days.length;
  const bar = Math.max(2, Math.min(28, slot - Math.max(2, slot * 0.3)));
  const x = (i: number) => PAD.left + i * slot + (slot - bar) / 2;
  const y = (v: number) => H - PAD.bottom - (v / ceiling) * (H - PAD.top - PAD.bottom);
  const ticks = [0, ceiling / 2, ceiling];
  const labelEvery = days.length > 45 ? 14 : days.length > 14 ? 7 : 1;
  const point = hover === null ? null : days[hover];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 pt-2 sm:px-4">
        {DAY_SERIES.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-micro text-(--color-muted)">
            <span aria-hidden="true" className="h-2.5 w-2.5 rounded-sm" style={{ background: s.colour }} />
            {s.label}
          </span>
        ))}
      </div>

      <div className="relative px-1 sm:px-2">
        <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-labelledby={titleId} onMouseLeave={() => setHover(null)}>
          <title id={titleId}>
            Bot conversations per day by outcome, {formatIstDate(days[0]!.date)} to {formatIstDate(days[days.length - 1]!.date)}.
          </title>

          {ticks.map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} stroke="var(--color-line-dim)" strokeWidth="1" />
              <text x={PAD.left - 6} y={y(t) + 3} textAnchor="end" className="fill-(--color-faint)" style={{ fontSize: 10 }}>
                {Math.round(t)}
              </text>
            </g>
          ))}

          {days.map((d, i) => {
            let base = 0;
            return (
              <g key={d.date}>
                {DAY_SERIES.map((s) => {
                  const v = d[s.key];
                  if (v <= 0) return null;
                  const top = y(base + v);
                  const bottom = y(base);
                  base += v;
                  // A 1px gap in the surface colour between stacked segments, so
                  // two adjacent outcomes never read as one.
                  return (
                    <rect
                      key={s.key}
                      x={x(i)} y={top} width={bar} height={Math.max(0.5, bottom - top - 1)}
                      fill={s.colour} rx={1}
                    />
                  );
                })}
                {/* Hit target wider than the bar. */}
                <rect
                  x={PAD.left + i * slot} y={PAD.top} width={slot} height={H - PAD.top - PAD.bottom}
                  fill="transparent"
                  onMouseEnter={() => setHover(i)}
                />
                {i % labelEvery === 0 && (
                  <text x={x(i) + bar / 2} y={H - 8} textAnchor="middle" className="fill-(--color-faint)" style={{ fontSize: 10 }}>
                    {shortDay(d.date)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {point && (
          <div
            className="pointer-events-none absolute top-1 rounded-md border border-(--color-line) bg-(--color-surface) px-2 py-1 text-micro shadow-sm"
            style={{ left: `${Math.min(80, Math.max(0, ((x(hover!) + bar / 2) / W) * 100))}%` }}
          >
            <p className="font-medium">{formatIstDate(point.date)} · {point.total.toLocaleString('en-IN')}</p>
            {DAY_SERIES.filter((s) => point[s.key] > 0).map((s) => (
              <p key={s.key} className="text-(--color-muted)">{s.label} {point[s.key]}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** When in the day customers talk to the bot, IST, one bar per hour. */
function HoursChart({ hours, total }: { hours: number[]; total: number }) {
  const titleId = useId();
  if (hours.length !== 24 || total === 0) return null;

  const peak = Math.max(1, ...hours);
  const busiest = hours.indexOf(peak);
  const W = 360;
  const H = 110;
  const PAD = { top: 8, right: 4, bottom: 18, left: 4 };
  const slot = (W - PAD.left - PAD.right) / 24;
  const bar = slot - 2;
  const y = (v: number) => H - PAD.bottom - (v / peak) * (H - PAD.top - PAD.bottom);

  return (
    <section className="min-w-0 rounded-lg border border-(--color-line) bg-(--color-surface) px-3 py-3 sm:px-4">
      <h2 className="text-body font-semibold">When they write</h2>
      <p className="mt-0.5 text-micro text-(--color-muted)">
        Busiest around {hourLabel(busiest)} — {Math.round((peak / total) * 100)}% of conversations end in that hour. Useful for staffing the inbox.
      </p>
      <svg viewBox={`0 0 ${W} ${H}`} className="mt-2 h-auto w-full" role="img" aria-labelledby={titleId}>
        <title id={titleId}>Conversations by hour of day, IST.</title>
        {hours.map((v, h) => (
          <g key={h}>
            <rect
              x={PAD.left + h * slot + 1} y={y(v)} width={bar} height={Math.max(0, H - PAD.bottom - y(v))}
              fill="var(--chart-1)" opacity={h === busiest ? 1 : 0.55} rx={1}
            />
            {h % 6 === 0 && (
              <text x={PAD.left + h * slot + 1} y={H - 5} className="fill-(--color-faint)" style={{ fontSize: 9 }}>
                {hourLabel(h)}
              </text>
            )}
          </g>
        ))}
      </svg>
    </section>
  );
}

function StepList({ title, hint, steps, of, colour, empty }: {
  title: string; hint: string; steps: BotReportStep[]; of: number; colour: string; empty: string;
}) {
  const top = steps[0]?.count ?? 0;
  return (
    <section className="min-w-0 rounded-lg border border-(--color-line) bg-(--color-surface) px-3 py-3 sm:px-4">
      <h2 className="text-body font-semibold">{title}</h2>
      <p className="mt-0.5 text-micro text-(--color-muted)">{hint}</p>
      {steps.length === 0 ? (
        <p className="mt-3 text-small text-(--color-muted)">{empty}</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-1.5">
          {steps.map((s) => (
            <li key={s.node} className="flex items-center gap-3 text-small">
              <span className="min-w-0 flex-1 truncate" title={s.node}>{s.label}</span>
              <span className="shrink-0 tabular-nums text-(--color-muted)">
                {s.count.toLocaleString('en-IN')}
                {of > 0 && <span className="ml-1 text-micro text-(--color-faint)">{Math.round((s.count / of) * 100)}%</span>}
              </span>
              <span className="h-1.5 w-20 shrink-0 rounded-full bg-(--color-sunken)" role="img" aria-label={`${s.count} of ${of}`}>
                <span className="block h-full rounded-full" style={{ width: `${Math.max(4, Math.round((s.count / Math.max(1, top)) * 100))}%`, background: colour }} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function describeMinutes(m: number): string {
  if (m < 1) return 'under a minute';
  if (m < 60) return `${Math.round(m)} min`;
  const h = Math.floor(m / 60);
  const rest = Math.round(m - h * 60);
  return rest ? `${h} h ${rest} min` : `${h} h`;
}

function hourLabel(h: number): string {
  const suffix = h < 12 ? 'am' : 'pm';
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return `${twelve}${suffix}`;
}

function shortDay(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  return `${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
}

function niceCeiling(peak: number): number {
  if (peak <= 5) return 5;
  if (peak <= 10) return 10;
  const magnitude = 10 ** Math.floor(Math.log10(peak));
  const unit = magnitude / 2;
  return Math.ceil(peak / unit) * unit;
}
